import type { RectShape } from '@model-framework/shape';
import type { StickyZoneId, ViewModelId } from 'balus-common';
import type { StickyZoneKey } from '@view-model/domain/key';
import type { ViewEntity } from '@view-model/domain/view/ViewEntity';
import { Point } from '@view-model/models/common/basic';
import type { Rect } from '@view-model/models/common/basic/Rect';
import type { ThemeColor } from '@view-model/models/common/color';
import type { TextAlign } from '@view-model/models/common/text-align';
import { DraggableContainer } from '@view-model/models/common/components/DraggableContainer';
import type { Position } from '@view-model/models/common/types/ui';
import type { FontSize } from '@view-model/models/framework/text';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStickyZoneContext } from './adapter';
import { StickyZoneContent, StickyZoneMenu } from './components';
import { StickyZoneShape } from './domain';
import type { StickyZone, StickyZoneTitlePosition } from './domain';
import { useNodeEditingUser } from '../editing-users/useNodeEditingUser';
import type { DragContext } from '@model-framework/ui';
import { selectAtom } from 'jotai/utils';
import { dropTargetViewAtom } from '@view-model/adapter/dropTargetViewAtom';
import { useAtomValue } from 'jotai/index';
import { useHandler } from '@framework/hooks';
import { ElementDescriptionOperation } from '../ElementDescription';
import { ElementDescriptionTarget } from '../ElementDescription/domain/ElementDescriptionTarget';
import { useCommandManager } from '@model-framework/command';
import type { UserPublicProfile } from '@user/PublicProfile';
import { HyperLinkShow, HyperLinkViewModelIcon } from '@model-framework/hyperlink';
import { useRightClickMenuOpenState } from '@user/pages/ViewModelPage/useRightClickMenuOpenState';
import { RightClickMenu } from '@framework/ui/elements/RightClickMenu';
import {
  ColorMenuButton,
  HyperLinkMenuButton,
  TextAlignMenuButton,
  TextSizeMenuButton,
  ZoneShapeMenuButton,
  ZoneTitlePositionMenuButton,
} from './components/StickyZoneRightClickMenuItem';

const MemoizedStickyZoneContent = memo(StickyZoneContent);

type Props = {
  readonly: boolean;
  viewModelId: ViewModelId;
  view: ViewEntity;
  stickyZone: StickyZone;
  position: Point;
  currentUserProfile: UserPublicProfile;
  isHoverSelected: boolean;
  isSelected: boolean;
  isMultiSelected: boolean;
  shape: RectShape;
  checkDraggingMultiSelectionDisabled: (e: MouseEvent) => boolean;
  showHoveringDragHandle: boolean;
  showMenu: boolean;
  onClick(id: StickyZoneId, e: React.MouseEvent<SVGGElement>): void;
  onDragStart(id: StickyZoneId, e: React.MouseEvent<SVGGElement>): void;
  onDrag(context: DragContext): void;
  onDragEnd(context: DragContext): void;
  onEditStart(id: StickyZoneId): void;
  onCreateNodeInZone(position: Position, zoneKey: StickyZoneKey): void;
  onLinkerStart(key: StickyZoneKey, startPosition: Position): void;
  onLinkerMove(currentPosition: Position): void;
  onLinkerEnd(currentPosition: Position): void;
  onClickViewModelLink(e: React.MouseEvent): void;
  onDelete(): void;
  onGroupSelectedZone(): void;
  onZoneToView(): void;
  onFontSizeSelected(fontSize: FontSize): void;
  onThemeColorSelected(themeColor: ThemeColor): void;
  onTextAlignSelected(textAlign: TextAlign): void;
  onZoneShapeSelected(newShape: StickyZoneShape): void;
  onZoneTitlePositionSelected(titlePosition: StickyZoneTitlePosition): void;
  onRectSelection(rect: Rect): void;
  onRectSelectionEnd(rect: Rect, zoneId: StickyZoneId): void;
};

type RightClickMenuButtonRef = {
  closePopup: () => void;
};

const DRAGGER_HEIGHT = 36;

export const StickyZoneView: React.FC<Props> = ({
  readonly,
  viewModelId,
  view,
  stickyZone,
  position,
  currentUserProfile,
  isHoverSelected,
  isSelected,
  isMultiSelected,
  shape,
  checkDraggingMultiSelectionDisabled,
  showHoveringDragHandle,
  showMenu,
  onClick,
  onDragStart,
  onDrag,
  onDragEnd,
  onEditStart,
  onCreateNodeInZone,
  onLinkerStart,
  onLinkerMove,
  onLinkerEnd,
  onClickViewModelLink,
  onDelete,
  onFontSizeSelected,
  onThemeColorSelected,
  onTextAlignSelected,
  onZoneShapeSelected,
  onZoneTitlePositionSelected,
  onRectSelection,
  onRectSelectionEnd,
  onGroupSelectedZone,
  onZoneToView,
}: Props) => {
  const { t } = useTranslation();
  const commandManager = useCommandManager();
  const { activeRightClickMenuIds, setActiveRightClickMenuIds } = useRightClickMenuOpenState();
  const [rightClickPosition, setRightClickPosition] = useState<Position | null>(null);

  const elementDescriptionOperation = useMemo(
    () =>
      new ElementDescriptionOperation(
        commandManager,
        currentUserProfile,
        viewModelId,
        view.modelId,
        ElementDescriptionTarget.Zone,
        stickyZone.id
      ),
    [commandManager, currentUserProfile, stickyZone.id, view.modelId, viewModelId]
  );

  const editingUser = useNodeEditingUser(stickyZone.key);

  const stickyZoneContext = useStickyZoneContext();

  const { width } = stickyZone.size.dump();

  const handleClick = useCallback(
    (e: React.MouseEvent<SVGGElement>) => {
      onClick(stickyZone.id, e);
    },
    [onClick, stickyZone.id]
  );

  const handleRightClick = useHandler((position: Position, e: React.MouseEvent<SVGGElement>): void => {
    // 選択状態にしてから右クリックメニューを表示する
    setRightClickPosition(position);
    onClick(stickyZone.id, e);
    setActiveRightClickMenuIds([stickyZone.id]);
    e.preventDefault();
  });

  const handleEditStart = useCallback(() => {
    onEditStart(stickyZone.id);
  }, [onEditStart, stickyZone.id]);

  const handleDragStart = useCallback(
    (e: React.MouseEvent<SVGGElement>) => {
      if (readonly) return;
      onDragStart(stickyZone.id, e);
    },
    [onDragStart, readonly, stickyZone.id]
  );

  const handleDrag = useCallback(
    (context: DragContext): void => {
      if (readonly) return;
      onDrag(context);
    },
    [onDrag, readonly]
  );

  const handleDragEnd = useCallback(
    (context: DragContext): void => {
      if (readonly) return;
      onDragEnd(context);
    },
    [onDragEnd, readonly]
  );

  const handleLinkerStart = useCallback(
    (startPosition: Position): void => {
      onLinkerStart(stickyZone.key, position.add(startPosition));
    },
    [onLinkerStart, position, stickyZone.key]
  );

  const handleLinkerMove = useCallback(
    (currentPosition: Position): void => {
      onLinkerMove(position.add(currentPosition));
    },
    [onLinkerMove, position]
  );

  const handleLinkerEnd = useCallback(
    (currentPosition: Position): void => {
      onLinkerEnd(position.add(currentPosition));
    },
    [onLinkerEnd, position]
  );

  const menuOffset = useMemo(() => {
    const { width } = stickyZone.size.dump();
    return new Point(position.x + width / 2 + 32, position.y);
  }, [stickyZone.size, position]);

  const draggerRectShape = useMemo(
    () =>
      shape.resize({
        height: DRAGGER_HEIGHT,
      }),
    [shape]
  );

  const handleSaveHyperLink = useHandler((url: string | null): void => {
    stickyZoneContext.handleEditingURLChanged(url, stickyZone.url);
  });

  const isDropTargetAtom = useMemo(
    () =>
      selectAtom(
        dropTargetViewAtom,
        (dropTarget) => dropTarget?.viewId === view.id && dropTarget.stickyZoneIds.includes(stickyZone.id)
      ),
    [stickyZone.id, view.id]
  );
  const isDropTarget = useAtomValue(isDropTargetAtom);

  const handleToggleZoneDescription = useHandler(() => {
    elementDescriptionOperation.toggle();
  });

  const textSizeMenuButtonRef = useRef<RightClickMenuButtonRef | null>(null);
  const colorMenuButtonRef = useRef<RightClickMenuButtonRef | null>(null);
  const textAlignMenuButtonRef = useRef<RightClickMenuButtonRef | null>(null);
  const hyperLinkMenuButtonRef = useRef<RightClickMenuButtonRef | null>(null);
  const zoneShapeMenuButtonRef = useRef<RightClickMenuButtonRef | null>(null);
  const zoneTitlePositionMenuButtonRef = useRef<RightClickMenuButtonRef | null>(null);

  const closeOtherRightClickSubMenu = useHandler((menuType: string): void => {
    if (menuType !== 'textSize') textSizeMenuButtonRef.current?.closePopup();
    if (menuType !== 'color') colorMenuButtonRef.current?.closePopup();
    if (menuType !== 'textAlign') textAlignMenuButtonRef.current?.closePopup();
    if (menuType !== 'hyperlink') hyperLinkMenuButtonRef.current?.closePopup();
    if (menuType !== 'zoneShape') zoneShapeMenuButtonRef.current?.closePopup();
    if (menuType !== 'zoneTitlePosition') zoneTitlePositionMenuButtonRef.current?.closePopup();
  });

  return (
    <>
      <DraggableContainer
        position={position}
        onClick={handleClick}
        onDrag={handleDrag}
        onDragMoveStarted={handleDragStart}
        onDragMoveEnded={handleDragEnd}
        onRightClick={view.setting.isLocked ? undefined : handleRightClick}
        dragHandleContent={
          isSelected ? (
            <>
              <g className={stickyZone.style.shape === StickyZoneShape.Circle ? 'opacity-0' : undefined}>
                {draggerRectShape.render({
                  className: 'fill-black/10 stroke-black/20',
                  strokeWidth: 4,
                  cursor: 'move',
                })}
              </g>
            </>
          ) : null
        }
        hoveringDragHandleContent={
          showHoveringDragHandle ? (
            <g className={stickyZone.style.shape === StickyZoneShape.Circle ? 'opacity-0' : undefined}>
              {draggerRectShape.render({
                className: 'fill-black/5 stroke-black/10',
                strokeWidth: 4,
                cursor: 'move',
              })}
            </g>
          ) : null
        }
      >
        <MemoizedStickyZoneContent
          readonly={readonly}
          stickyZone={stickyZone}
          position={position}
          isHoverSelected={isHoverSelected || isDropTarget}
          isSelected={isSelected}
          isMultiSelected={isMultiSelected}
          showMenu={showMenu}
          editing={stickyZoneContext.myEditing}
          editingUser={editingUser}
          onEditStart={handleEditStart}
          onDblClickZone={onCreateNodeInZone}
          onLinkerStart={handleLinkerStart}
          onLinkerMove={handleLinkerMove}
          onLinkerEnd={handleLinkerEnd}
          onRectSelection={onRectSelection}
          onRectSelectionEnd={onRectSelectionEnd}
          shape={shape}
          checkDraggingMultiSelectionDisabled={checkDraggingMultiSelectionDisabled}
        />
        {/* 選択時のハイパーリンク表示 */}
        {isSelected && stickyZone.url && (
          <HyperLinkShow
            x={width + 25}
            y={0}
            url={stickyZone.url}
            viewModelId={viewModelId}
            onClickViewModelLink={onClickViewModelLink}
          />
        )}

        {/* 付箋左上のアイコン表示 */}
        {stickyZone.url && <HyperLinkViewModelIcon url={stickyZone.url} />}
      </DraggableContainer>

      {showMenu && (
        <StickyZoneMenu
          currentStyle={stickyZone.style}
          offset={menuOffset}
          textEditing={stickyZoneContext.myEditing}
          url={stickyZone.url}
          onFontSizeSelected={onFontSizeSelected}
          onThemeColorSelected={onThemeColorSelected}
          onTextAlignSelected={onTextAlignSelected}
          onZoneShapeSelected={onZoneShapeSelected}
          onZoneTitlePositionSelected={onZoneTitlePositionSelected}
          onToggleZoneDescription={handleToggleZoneDescription}
          onSaveHyperLink={handleSaveHyperLink}
          onDeleteZone={onDelete}
          onGroupSelectedZone={onGroupSelectedZone}
          onZoneToView={onZoneToView}
        />
      )}

      <RightClickMenu
        isOpen={activeRightClickMenuIds.length === 1 && activeRightClickMenuIds[0] === stickyZone.id}
        mousePosition={rightClickPosition}
      >
        <TextSizeMenuButton
          ref={textSizeMenuButtonRef}
          onChangeTextSize={onFontSizeSelected}
          onOpenSubMenu={closeOtherRightClickSubMenu}
        />
        <ColorMenuButton
          ref={colorMenuButtonRef}
          onChangeColor={onThemeColorSelected}
          onOpenSubMenu={closeOtherRightClickSubMenu}
        />
        <TextAlignMenuButton
          ref={textAlignMenuButtonRef}
          onChangeTextAlign={onTextAlignSelected}
          onOpenSubMenu={closeOtherRightClickSubMenu}
        />
        <ZoneShapeMenuButton
          ref={zoneShapeMenuButtonRef}
          onChangeShape={onZoneShapeSelected}
          onOpenSubMenu={closeOtherRightClickSubMenu}
        />
        <ZoneTitlePositionMenuButton
          ref={zoneTitlePositionMenuButtonRef}
          onChangeTitlePosition={onZoneTitlePositionSelected}
          onOpenSubMenu={closeOtherRightClickSubMenu}
        />
        <HyperLinkMenuButton
          ref={hyperLinkMenuButtonRef}
          onClose={() => {
            setActiveRightClickMenuIds([]);
          }}
          initialURL={stickyZone.url}
          onSave={(url: string | null) => {
            handleSaveHyperLink(url);
            setActiveRightClickMenuIds([]);
          }}
          onOpenSubMenu={closeOtherRightClickSubMenu}
        />
        <div onClick={handleToggleZoneDescription} className="w-full cursor-pointer px-12 py-4 hover:bg-gray-200">
          {t('canvas.menu.note')}
        </div>
        <div onClick={onGroupSelectedZone} className="w-full cursor-pointer px-12 py-4 hover:bg-gray-200">
          {t('canvas.menu.group')}
        </div>
        <div onClick={onZoneToView} className="w-full cursor-pointer px-12 py-4 hover:bg-gray-200">
          {t('canvas.menu.zone.convertToView')}
        </div>
        <div onClick={onDelete} className="w-full cursor-pointer px-12 py-4 hover:bg-gray-200">
          {t('common.action.delete')}
        </div>
      </RightClickMenu>
    </>
  );
};
